import { useState } from 'react'
import Header from '../components/Header'
import Footer from '../components/Footer'
import { ArrowLeft, Building, Calendar, Users, Award, Trophy, Briefcase, Settings, X } from 'lucide-react'
import { QUALIFICATION_IMAGES } from '../config/assets'

const {
  PATENT_1: PatentImage,
  PATENT_2: PatentImage2,
  PATENT_3: PatentImage3,
  PATENT_4: PatentImage4,
  PATENT_5: PatentImage5,
  PATENT_6: PatentImage6,
  PATENT_7: PatentImage7,
  PATENT_8: PatentImage8,
  PATENT_9: PatentImage9,
  PATENT_10: PatentImage10,
  PATENT_11: PatentImage11,
  PATENT_12: PatentImage12,
  PATENT_13: PatentImage13
} = QUALIFICATION_IMAGES

const navItems = [
  { id: 'overview', label: '公司简介', icon: Building },
  { id: 'history', label: '发展历程', icon: Calendar },
  { id: 'business', label: '主营业务', icon: Briefcase },
  { id: 'organization', label: '组织架构', icon: Users },
  { id: 'intellectual', label: '知识产权', icon: Trophy },
  { id: 'operation', label: '经营状况', icon: Settings },
  { id: 'honors', label: '荣誉资质', icon: Award },
]

const certificationImages = [
  PatentImage, PatentImage2, PatentImage3, PatentImage4, PatentImage5,
  PatentImage6, PatentImage7, PatentImage8, PatentImage9, PatentImage10,
  PatentImage11, PatentImage12, PatentImage13
]

const overviewContent = {
  companyName: '山东云净环保科技有限公司',
  established: '2016年3月31日',
  legalRepresentative: '伏开顺',
  registeredCapital: '2000万元（实缴420万元）',
  companyType: '有限责任公司（自然人投资或控股）',
  address: '山东省临沂市河东经济开发区芝麻墩社区西兰墩村村西',
  employees: '6人（2024年）',
  businessScope: '环境治理设备、环保节能设备、采暖炉具、新能源供暖设备、制冷设备、金属制品、环境污染治理药剂等的研发、制造、销售、安装及维护',
  mainProducts: '生物质炊事采暖炉、生物质颗粒取暖炉、常压锅炉、燃气采暖设备、电采暖设备、水源/空气源热泵、净水设备、空气净化器、新风系统',
  achievements: [
    { year: '2023年', text: '被认定为高新技术企业（编号GR202337008279，有效期至2026年12月7日）' },
    { year: '2023年', text: '被认定为山东省创新型中小企业' },
    { year: '2025年', text: '获得"科技型中小企业"资质（编号2025371312A8037951）' },
    { year: '2021年/2023年/2024年', text: '获得纳税人信用A级评价' },
  ],
}



export default function CompanyDetailPage() {
  const [activeSection, setActiveSection] = useState('overview')
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const [selectedCertImage, setSelectedCertImage] = useState<string | null>(null)

  const scrollToSection = (id: string) => {
    setActiveSection(id)
    setIsSidebarOpen(false)
    const element = document.getElementById(id)
    if (element) {
      const offset = 100
      const elementPosition = element.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.pageYOffset - offset
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      })
    }
  }

  return (
    <div className="min-h-screen bg-secondary-50">
      <Header />

      {/* Hero Banner */}
      <section className="bg-gradient-to-r from-primary-700 to-primary-900 text-white py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-4 mb-4">
            <a href="/about" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors text-sm">
              <ArrowLeft className="w-4 h-4" />
              返回
            </a>
          </div>
          <h1 className="text-3xl font-bold">{overviewContent.companyName}</h1>
          <div className="flex flex-wrap gap-4 mt-3 text-sm text-white/70">
            <span>法定代表人：{overviewContent.legalRepresentative}</span>
            <span>注册资本：{overviewContent.registeredCapital}</span>
            <span>成立时间：{overviewContent.established}</span>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex gap-8">
          {/* Left Sidebar - Navigation */}
          <aside className="hidden lg:block w-56 shrink-0">
            <div className="sticky top-24 bg-white rounded-xl shadow-sm border border-secondary-100 overflow-hidden">
              <div className="p-4 border-b border-secondary-100">
                <h3 className="font-semibold text-secondary-900">目录</h3>
              </div>
              <nav className="py-2">
                {navItems.map(item => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
                      activeSection === item.id
                        ? 'bg-primary-50 text-primary-700 border-r-2 border-primary-600'
                        : 'text-secondary-600 hover:bg-secondary-50'
                    }`}
                  >
                    <item.icon className="w-4 h-4" />
                    {item.label}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Mobile Sidebar Toggle */}
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="lg:hidden fixed bottom-6 right-6 z-50 w-12 h-12 bg-primary-600 text-white rounded-full shadow-lg flex items-center justify-center"
          >
            <Settings className="w-5 h-5" />
          </button>

          {/* Mobile Sidebar */}
          {isSidebarOpen && (
            <div className="lg:hidden fixed inset-0 z-40">
              <div className="absolute inset-0 bg-black/50" onClick={() => setIsSidebarOpen(false)} />
              <div className="absolute right-0 top-0 bottom-0 w-64 bg-white shadow-xl overflow-y-auto">
                <div className="p-4 border-b border-secondary-100 flex items-center justify-between">
                  <h3 className="font-semibold text-secondary-900">目录</h3>
                  <button onClick={() => setIsSidebarOpen(false)} className="text-secondary-500">
                    ✕
                  </button>
                </div>
                <nav className="py-2">
                  {navItems.map(item => (
                    <button
                      key={item.id}
                      onClick={() => scrollToSection(item.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
                        activeSection === item.id
                          ? 'bg-primary-50 text-primary-700'
                          : 'text-secondary-600 hover:bg-secondary-50'
                      }`}
                    >
                      <item.icon className="w-4 h-4" />
                      {item.label}
                    </button>
                  ))}
                </nav>
              </div>
            </div>
          )}

          {/* Main Content Area */}
          <main className="flex-1 min-w-0">
            {/* Overview Section */}
            <section id="overview" className="bg-white rounded-xl shadow-sm border border-secondary-100 p-6 mb-6">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-secondary-100">
                <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center">
                  <Building className="w-4 h-4 text-primary-600" />
                </div>
                <h2 className="text-xl font-bold text-secondary-900">公司简介</h2>
              </div>

              <div className="space-y-6">
                <p className="text-secondary-600 leading-relaxed text-indent">
                  {overviewContent.companyName}成立于{overviewContent.established}，是一家专注于清洁能源利用技术研发与应用的高新技术企业。公司总部位于{overviewContent.address}。
                </p>
                <p className="text-secondary-600 leading-relaxed text-indent">
                  {overviewContent.businessScope}。凭借先进的技术和优质的产品，公司业绩遍布全国20多个省市地区。
                </p>

                <div className="bg-secondary-50 rounded-xl p-5">
                  <h3 className="font-semibold text-secondary-900 mb-4">基本信息</h3>
                  <div className="grid sm:grid-cols-2 gap-4 text-sm">
                    <div className="flex items-start gap-2">
                      <span className="text-secondary-500 w-20">企业类型：</span>
                      <span className="text-secondary-900">{overviewContent.companyType}</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-secondary-500 w-20">注册资本：</span>
                      <span className="text-secondary-900">{overviewContent.registeredCapital}</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-secondary-500 w-20">成立时间：</span>
                      <span className="text-secondary-900">{overviewContent.established}</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-secondary-500 w-20">参保人数：</span>
                      <span className="text-secondary-900">{overviewContent.employees}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold text-secondary-900 mb-4">主要产品</h3>
                  <p className="text-secondary-600 leading-relaxed text-indent">{overviewContent.mainProducts}</p>
                </div>

                <div>
                  <h3 className="font-semibold text-secondary-900 mb-4">企业荣誉</h3>
                  <div className="space-y-3">
                    {overviewContent.achievements.map((item, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <div className="w-2 h-2 rounded-full bg-primary-500 mt-2 shrink-0" />
                        <div>
                          <span className="text-primary-600 font-medium">{item.year}</span>
                          <span className="text-secondary-600"> - {item.text}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* History Section */}
            <section id="history" className="bg-white rounded-xl shadow-sm border border-secondary-100 p-6 mb-6">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-secondary-100">
                <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center">
                  <Calendar className="w-4 h-4 text-primary-600" />
                </div>
                <h2 className="text-xl font-bold text-secondary-900">发展历程</h2>
              </div>

              <div className="text-secondary-700 leading-relaxed">
                <p className="text-indent">
                  山东云净环保科技有限公司成立于2016年3月31日，初始注册资本为2000万元。2023年，公司被认定为高新技术企业（编号GR202337008279）和山东省创新型中小企业。同年4月28日，公司成立吉林分公司；12月27日，"云净炉业"商标（注册号76054435）注册成功。2024年，公司于5月16日成立天津分公司；2025年，公司获得"科技型中小企业"资质（编号2025371312A8037951），并于7月31日成立庆阳分公司。同年，公司还提交了"火烈鸟"、"火灵珠"、"天天顺"、"福小暖"等多个商标的注册申请。
                </p>
              </div>
            </section>

            {/* Business Section */}
            <section id="business" className="bg-white rounded-xl shadow-sm border border-secondary-100 p-6 mb-6">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-secondary-100">
                <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center">
                  <Briefcase className="w-4 h-4 text-primary-600" />
                </div>
                <h2 className="text-xl font-bold text-secondary-900">主营业务</h2>
              </div>

              <div className="text-secondary-700 leading-relaxed space-y-4">
                <p className="text-indent">
                  山东云净环保科技有限公司主要从事环境治理设备、环保节能设备、采暖炉具、新能源供暖设备、制冷设备、金属制品、环境治理药剂等的研发、制造、销售、安装及维护，同时具备电器工程自动化设计能力，业绩遍布全国20多个省市地区。
                </p>
                <p className="text-indent">
                  公司主要产品包括生物质炊事采暖炉、生物质颗粒取暖炉、常压锅炉、燃气采暖设备、电采暖设备、水源/空气源热泵、净水设备、空气净化器、新风系统等，其中生物质炊事采暖炉等产品曾应用于平邑县2022年农村地区清洁取暖项目。
                </p>
                <p className="text-indent">
                  此外，公司还从事五金交电、家用电器、电子产品、钢材、耐火材料、环境污染治理药剂、车用尿素、尿素化肥（不含危化品）的销售，以及广告设计、金属材料加工、货物及技术进出口等业务。
                </p>
              </div>
            </section>

            {/* Organization Section */}
            <section id="organization" className="bg-white rounded-xl shadow-sm border border-secondary-100 p-6 mb-6">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-secondary-100">
                <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center">
                  <Users className="w-4 h-4 text-primary-600" />
                </div>
                <h2 className="text-xl font-bold text-secondary-900">组织架构</h2>
              </div>

              <div className="text-secondary-700 leading-relaxed">
                <p className="text-indent">
                  公司共设立7家分公司，其中存续状态的分公司包括山东云净环保科技有限公司庆阳分公司（负责人伏开顺，2025年7月31日成立）、天津分公司（负责人岳春伟，2024年5月16日成立，地址为天津市宝坻区牛家牌镇）、永吉分公司（负责人伏开顺，2024年1月25日成立）、吉林分公司（负责人卢敏，2023年4月28日成立，地址为吉林市昌邑区）。
                </p>
              </div>
            </section>

            {/* Intellectual Property Section */}
            <section id="intellectual" className="bg-white rounded-xl shadow-sm border border-secondary-100 p-6 mb-6">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-secondary-100">
                <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center">
                  <Trophy className="w-4 h-4 text-primary-600" />
                </div>
                <h2 className="text-xl font-bold text-secondary-900">知识产权</h2>
              </div>

              <div className="grid lg:grid-cols-2 gap-8">
                {/* Left: Text Content */}
                <div className="text-secondary-700 leading-relaxed space-y-4">
                  <p className="text-indent">
                    山东云净环保科技有限公司拥有专利11项、注册商标13项。公司专利主要为实用新型和外观设计专利，核心围绕生物质采暖炉、烟气处理、送料装置等环保节能设备技术，包括一种双绞龙送料装置（专利号：CN213169955U，授权日：2021-05-11）、一种生物质颗粒取暖炉烟气处理装置（专利号：CN202322365456.5，授权日：2024-03-22）、取暖炉（多功能生物质）（外观设计专利，申请号：CN202530201184.7，授权日：2025-11-14）、一种新型燃烧炉交叉水循环装置（专利号：CN202322575893.X）、一种新型多功能水暖燃烧壁炉（专利号：CN202322505192.9）和一种生物质颗粒取暖炉（专利号：CN202322575893.X）等。
                  </p>
                  <p className="text-indent">
                    公司商标包括"云净炉业"（注册号：76054435，国际分类11类）已注册，以及"火烈鸟"、"火灵珠"、"天天顺"、"福小暖"等商标处于等待实质审查阶段。
                  </p>
                </div>

                {/* Right: Certification Images */}
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                  {certificationImages.slice(5).map((image, index) => (
                    <div
                      key={index}
                      className="relative overflow-hidden rounded-lg border border-secondary-200 cursor-pointer hover:shadow-md transition-all transform hover:-translate-y-1 h-20 sm:h-24"
                      onClick={() => setSelectedCertImage(image)}
                    >
                      <img
                        src={image}
                        alt={`专利证书${index + 6}`}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-30 transition-all flex items-center justify-center">
                        <span className="text-white opacity-0 hover:opacity-100 transition-opacity text-xs font-medium">查看</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Operation Status Section */}
            <section id="operation" className="bg-white rounded-xl shadow-sm border border-secondary-100 p-6 mb-6">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-secondary-100">
                <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center">
                  <Settings className="w-4 h-4 text-primary-600" />
                </div>
                <h2 className="text-xl font-bold text-secondary-900">经营状况</h2>
              </div>

              <div className="text-secondary-700 leading-relaxed space-y-4">
                <p className="text-indent">
                  山东云净环保科技有限公司成立于2016年3月31日，注册资本为2000万元，实缴资本420万元，2024年参保人数为6人，企业规模为小型。公司注册资本历经多次变更，2024年8月由5500万元减至2000万元，股东出资额同步调整。
                </p>
                <p className="text-indent">
                  2021年营业收入为186.20万元，累计参与招投标项目52次，中标34次。公司承接的代表性项目包括平邑县2022年农村地区清洁取暖项目（郑城镇、白彦镇生物质炊事采暖炉），中标金额236.775万元；合水县农村生物质燃料水暖清洁取暖项目；吉林市龙潭区农村分散式清洁取暖替代项目；兰陵县2024年农村地区清洁取暖设备采购项目等。
                </p>
                <p className="text-indent">
                  公司共有7家分支机构，其中山东云净环保科技有限公司庆阳分公司、天津分公司、永吉分公司、吉林分公司为存续状态。
                </p>
                <p className="text-indent">
                  公司拥有资质证书13个，行政许可3个，并被认定为高新技术企业、科技型中小企业及创新型中小企业。
                </p>
              </div>
            </section>

            {/* Honors Section */}
            <section id="honors" className="bg-white rounded-xl shadow-sm border border-secondary-100 p-6 mb-6">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-secondary-100">
                <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center">
                  <Award className="w-4 h-4 text-primary-600" />
                </div>
                <h2 className="text-xl font-bold text-secondary-900">荣誉资质</h2>
              </div>

              <div className="grid lg:grid-cols-2 gap-8">
                {/* Left: Text Content */}
                <div className="text-secondary-700 leading-relaxed space-y-4">
                  <p className="text-indent">
                    公司于2023年被认定为高新技术企业，编号GR202337008279，有效期至2026年12月7日，认定机构为全国高新技术企业认定管理工作领导小组办公室；2025年入库科技型中小企业，编号2025371312A8037951，认定机构为山东省科学技术厅；2023年被认定为创新型中小企业，有效期至2026年12月26日，认定机构为山东省工业和信息化厅。
                  </p>
                  <p className="text-indent">
                    公司在2021年、2023年、2024年获得纳税人信用A级评价。
                  </p>
                </div>

                {/* Right: Certification Images */}
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                  {certificationImages.slice(0, 5).map((image, index) => (
                    <div
                      key={index}
                      className="relative overflow-hidden rounded-lg border border-secondary-200 cursor-pointer hover:shadow-md transition-all transform hover:-translate-y-1"
                      onClick={() => setSelectedCertImage(image)}
                    >
                      <img
                        src={image}
                        alt={`企业资质${index + 1}`}
                        className="w-full h-24 sm:h-28 object-cover"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-30 transition-all flex items-center justify-center">
                        <span className="text-white opacity-0 hover:opacity-100 transition-opacity text-xs font-medium">查看</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

          </main>
        </div>
      </div>

      {/* Certification Image Viewer */}
      {selectedCertImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4">
          <button
            onClick={() => setSelectedCertImage(null)}
            className="absolute top-4 right-4 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
          <button
            onClick={() => setSelectedCertImage(null)}
            className="absolute inset-0"
          />
          <img
            src={selectedCertImage}
            alt="企业资质证书"
            className="max-w-5xl max-h-[85vh] object-contain rounded-lg"
          />
        </div>
      )}

      <Footer />
    </div>
  )
}